import * as React from 'react';

import { observer, inject } from 'mobx-react';
import { BloodworkEntryStore } from '../stores/bloodwork-entry-store';
import { FurtherConsiderationsEntryStore } from '../stores/further-considerations-entry-store';
import { Container, Button, Form, Col, Card, Row } from 'react-bootstrap';
import { ClientEntryStore } from '../stores/client-entry-store';
import { Bloodwork, Client } from '../models';
import { BloodworkDetailsModal } from '../components/bloodworkz/bloodwork-details-modal';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';
import { BloodworkDisplay } from '../components/bloodworkz/bloodwork-display';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UserStore } from '../stores/user-store';
import { BloodworkComparisonAnalysis } from '../components/bloodworkz/bloodwork-comparison-analysis';

interface BloodworkAnalysisContainerProps {
  userStore?: UserStore;
  userProfileUIStore?: UserProfileUIStore;
  bloodworkEntryStore?: BloodworkEntryStore;
  clientEntryStore?: ClientEntryStore;
  furtherConsiderationsEntryStore?: FurtherConsiderationsEntryStore;
  history?: any;
}

type State = {
  clientId: string;
  selectedClient: Client;
  selectedBloodwork: Bloodwork;
  bloodwork: Bloodwork;
  showBloodworkDetailsModal: boolean;
  showClientBloodworks: boolean;
};

@inject(
  'userStore',
  'userProfileUIStore',
  'bloodworkEntryStore',
  'clientEntryStore',
  'furtherConsiderationsEntryStore',
)
@observer
export class BloodworkAnalysisContainer extends React.Component<
  BloodworkAnalysisContainerProps,
  {}
> {
  state: State = {
    clientId: '',
    selectedClient: undefined,
    selectedBloodwork: undefined,
    bloodwork: undefined,
    showBloodworkDetailsModal: false,
    showClientBloodworks: true,
  };

  componentWillMount() {
    const {
      clientEntryStore,
      furtherConsiderationsEntryStore,
      bloodworkEntryStore,
      userProfileUIStore,
      history,
    } = this.props;
    bloodworkEntryStore.loadBloodworkRanges();

    const stateClientId =
      history && history.location && history.location.state
        ? history.location.state.clientId
        : null;

    clientEntryStore.loadCurrentClients().then(() => {
      if (stateClientId) {
        this.setState({ clientId: stateClientId }, () => {
          this.loadResults();
        });
      }
    });

    furtherConsiderationsEntryStore.loadFurtherConsiderations();
    userProfileUIStore.loadCurrentUserProfile();
  }

  componentWillUnmount() {
    const { bloodworkEntryStore } = this.props;
    bloodworkEntryStore.resetClientBloodworks();
  }

  handleClientChange = (event: any) => {
    const { bloodworkEntryStore } = this.props;
    const clientId = event.target.value;

    bloodworkEntryStore.resetClientBloodworks();

    this.setState({
      clientId: clientId,
      selectedClient: undefined,
      selectedBloodwork: undefined,
    });
  };

  loadResults() {
    const { clientEntryStore, bloodworkEntryStore } = this.props;
    const { clientId } = this.state;
    const selectedClient = Client.findClient(
      clientId,
      clientEntryStore.clients,
    );
    bloodworkEntryStore.resetCurrentBloodwork();

    this.setState({
      selectedClient,
    });

    if (selectedClient) {
      bloodworkEntryStore.loadClientBloodworks(selectedClient.id);
    }
  }

  selectClient() {
    const { clientEntryStore } = this.props;
    if (clientEntryStore.clients === undefined) {
      return null;
    }

    return (
      <Card>
        <Card.Header>Select Client</Card.Header>
        <Card.Body>
          <Form noValidate>
            <Form.Group as={Row} controlId="clientGroup">
              <Form.Label column sm={1}>
                Client Name
              </Form.Label>
              <Col sm={3} lg={3}>
                <Form.Control
                  as="select"
                  name="clientId"
                  size="sm"
                  placeholder="Select Client"
                  value={this.state.clientId}
                  onChange={this.handleClientChange}
                >
                  <option value="">Select Client</option>
                  {clientEntryStore.clients.map(
                    (client: Client, index: number) => {
                      return (
                        <option key={index} value={client.id}>
                          {client.lastName}, {client.firstName}
                        </option>
                      );
                    },
                  )}
                </Form.Control>
              </Col>
              <Col>
                <Button
                  type="button"
                  onClick={() => {
                    this.loadResults();
                  }}
                >
                  <FontAwesomeIcon icon="table" />
                  &nbsp;&nbsp;Show Test Results
                </Button>
              </Col>
            </Form.Group>
          </Form>
        </Card.Body>
      </Card>
    );
  }

  viewResults(bloodwork: Bloodwork) {
    this.setState({
      bloodwork,
      selectedBloodwork: undefined,
      showBloodworkDetailsModal: true,
    });
  }

  onHideBloodworkDetails() {
    this.setState({
      showBloodworkDetailsModal: false,
    });
  }

  deleteBloodwork(bloodwork: Bloodwork) {
    const deleteBloodwork = window.confirm(
      'Are you sure you want to delete the test results?',
    );
    if (deleteBloodwork) {
      const { bloodworkEntryStore } = this.props;
      bloodworkEntryStore.deleteBloodwork(bloodwork);
      this.loadResults();
    }
  }
  onExportToPdf() {
    this.setState({
      showClientBloodworks: false,
    });
  }

  onHidePdfModal() {
    this.setState({
      showClientBloodworks: true,
    });
  }

  render() {
    const { selectedClient, showClientBloodworks } = this.state;
    const {
      userProfileUIStore,
      bloodworkEntryStore,
      furtherConsiderationsEntryStore,
      clientEntryStore,
    } = this.props;
    const clientBloodworks = bloodworkEntryStore.clientBloodworks;

    // NOTE: Using the state variable showClientBloodworks to fix an issue with PDF exports
    // when there are several client bloodworks displayed.
    return (
      <Container fluid>
        {this.selectClient()}

        {clientBloodworks.length > 0 && showClientBloodworks ? (
          <>
            <BloodworkDisplay
              history={this.props.history}
              currentUserProfile={userProfileUIStore.currentUserProfile}
              testResultsPanelLabel="Client Test Results"
              showPreviousOnCharts={true}
              bloodworks={clientBloodworks}
              bloodworkEntryStore={bloodworkEntryStore}
              refreshBloodworks={bloodworkEntryStore.refreshBloodworks}
              loadClientBloodworks={bloodworkEntryStore.loadClientBloodworks}
              furtherConsiderations={
                furtherConsiderationsEntryStore.furtherConsiderations
              }
              allowLoadMoreTestResults={false}
            />
          </>
        ) : selectedClient ? (
          <div>
            <br />
            &nbsp;&nbsp;No test results found.
          </div>
        ) : null}

        <BloodworkComparisonAnalysis
          bloodworks={clientBloodworks}
          bloodworkRanges={bloodworkEntryStore.bloodworkRanges}
          currentUserProfile={userProfileUIStore.currentUserProfile}
          onExportToPdf={() => this.onExportToPdf()}
          onHidePdfModal={() => this.onHidePdfModal()}
          bloodworkEntryStore={bloodworkEntryStore}
          clientEntryStore={clientEntryStore}
          furtherConsiderationsEntryStore={furtherConsiderationsEntryStore}
        />

        <BloodworkDetailsModal
          show={this.state.showBloodworkDetailsModal}
          bloodwork={this.state.bloodwork}
          bloodworkRanges={bloodworkEntryStore.bloodworkRanges}
          onHide={() => {
            this.onHideBloodworkDetails();
          }}
        />
      </Container>
    );
  }
}
